import Drawable from '../entities/Drawable.js';
import Sprite from './Sprite.js';
import { imageStore } from '../images.js';
import PokemonIcon from './PokemonIcon.js';
import PokemonSprite from './PokemonSprite.js';
import TypeBadge from './TypeBadge.js';
import OptionsBox from './OptionsBox.js';
import Pokemon from '../entities/Pokemon.js';
import { getOSM } from '../OverworldAnimation.js';
import { PC_POKEMON_PRESELECTED } from '../constants.js';
import PixelText from './PixelText.js';

export default class PCMenu extends Drawable {
    constructor({ stateMachine, position = { x: 0, y: 0 }, visible = false }) {
        super();
        this.stateMachine = stateMachine;
        this.visible = visible;
        this.position = position;
        this.background = new Sprite({
            position: this.position,
            image: imageStore.get('img/pc_menu.png'),
            moving: false,
            visible: true,
        });
        this.boxArrows = new Sprite({
            position: {
                x: 84,
                y: 7,
            },
            image: imageStore.get('img/pc_menu_arrows.png'),
            moving: false,
            visible: true,
            frames: {
                max: 8,
                val: 0,
            },
        });
        this.cursor = new Sprite({
            position: this.position,
            image: imageStore.get('img/pc_cursor.png'),
            visible: true,
            moving: false,
            frames: {
                max: 2,
                val: 0,
            },
        });
        this.boxTitle = new PixelText({
            text: `Box ${this.stateMachine.pcMenu.box}`,
            position: { x: 145, y: 17 - 7 },
            color: 'white',
        });

        this.pcElectricityOff = new Sprite({
            image: imageStore.get('img/pc_electricity_off.png'),
            visible: true,
            moving: true,
            frames: {
                max: 1,
                val: 0,
            },
            position: {
                x: 2,
                y: 4,
            },
        });

        this.pcElectricityOn = new Sprite({
            image: imageStore.get('img/pc_electricity_on.png'),
            visible: true,
            moving: true,
            frames: {
                max: 3,
            },
            position: {
                x: 2,
                y: 4,
            },
        });

        this.currentPokemonNameText = new PixelText({
            text: this.stateMachine.pcMenu.currentPokemon?.name,
            position: { x: 4, y: 118 - 7 },
            color: 'white',
        });

        this.currentPokemonLevelText = new PixelText({
            text: `Lv. ${this.stateMachine.pcMenu.currentPokemon?.level}`,
            position: { x: 4, y: 128 - 7 },
            color: 'white',
        });
        this.currentPokemonNatureText = new PixelText({
            text: `${this.stateMachine.pcMenu.currentPokemon?.nature.displayName()} Nature`,
            position: { x: 4, y: 142 - 7 },
            color: 'white',
        });
        this.currentPokemonNatureSummaryText = new PixelText({
            text: '',
            position: { x: 4, y: 152 - 7 },
            color: 'white',
        });

        this.typeBadge1 = new TypeBadge({
            type: null,
            position: { x: 4, y: 90 },
            visible: true,
        });
        this.typeBadge2 = new TypeBadge({
            type: null,
            position: { x: 4 + 36, y: 90 },
            visible: true,
        });

        // Track cursor movement
        this.currentCursorPosition = { x: 0, y: 0 };
        this.targetCursorPosition = { x: 0, y: 0 };
        this.easingFactor = 0.3; // Controls how quickly it eases (0.1 - 0.3 is a good range)
        this.minDistance = 0.5; // When to consider movement complete
        this.isMoving = false;

        this.currentPokemonSprite = new PokemonSprite({
            pokemon: this.stateMachine.pcMenu.currentPokemon,
            position: { x: 8, y: 16 },
        });

        this.boxIcons = [
            [null, null, null, null, null, null],
            [null, null, null, null, null, null],
            [null, null, null, null, null, null],
            [null, null, null, null, null, null],
        ];

        this.selectedPokemonIcon = null;

        this.partyIcons = [null, null, null, null, null, null];
        this.stateMachine.player.party.forEach((pokemon, index) => {
            this.partyIcons[index] = new PokemonIcon({
                pokemon: pokemon,
                position: {
                    x: 80 + 24 * index,
                    y: 120,
                },
            });
        });

        this.pokemonPreselectedMenu = new OptionsBox({
            selectedOption: 0,
            options: ['Move', 'Summary', 'Release'],
            position: {
                x: 168,
                y: 90,
            },
            visible: true,
        });

        this.selectedPokemonIconShadow = new Sprite({
            position: {
                x: 0,
                y: 0,
            },
            image: imageStore.get('img/pc_icon_shadow.png'),
            moving: false,
            visible: true,
        });
    }

    calculateCursorPosition() {
        const currentSection = this.stateMachine.pcMenu.currentSection;
        const row = this.stateMachine.pcMenu.row;
        const column = this.stateMachine.pcMenu.column;

        let targetPosition;

        if (currentSection === 0) {
            // In box header
            targetPosition = {
                x: 148,
                y: -12,
            };
        } else if (currentSection === 1) {
            // In box
            const preliminaryX = 86;
            const preliminaryY = 12;
            targetPosition = {
                x: preliminaryX + column * 24,
                y: preliminaryY + row * 20,
            };
        } else if (currentSection === 2) {
            // In party
            const preliminaryX = 86;
            const preliminaryY = 110;
            targetPosition = {
                x: preliminaryX + column * 24,
                y: preliminaryY,
            };
        }

        // Update the target position
        this.targetCursorPosition = targetPosition;

        // If this is the first time, initialize current position to target
        if (!this.isMoving && !this.currentCursorPosition.x && !this.currentCursorPosition.y) {
            this.currentCursorPosition = { ...targetPosition };
        }

        this.isMoving = true;

        // Return the current position for drawing
        return this.currentCursorPosition;
    }

    updateCursorPosition() {
        if (!this.isMoving) return;

        // Calculate the distance for x and y
        const dx = this.targetCursorPosition.x - this.currentCursorPosition.x;
        const dy = this.targetCursorPosition.y - this.currentCursorPosition.y;

        // Calculate total distance
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < this.minDistance) {
            // We're close enough, just set to target exactly
            this.currentCursorPosition = { ...this.targetCursorPosition };
            this.isMoving = false;
        } else {
            // Apply easing to both x and y
            // The movement is proportional to the distance remaining
            // This creates a logarithmic-like deceleration pattern
            this.currentCursorPosition.x += dx * this.easingFactor;
            this.currentCursorPosition.y += dy * this.easingFactor;
        }
    }

    drawCurrentPokemon() {
        if (this.stateMachine.pcMenu.currentPokemon instanceof Pokemon) {
            this.currentPokemonSprite.pokemon = this.stateMachine.pcMenu.currentPokemon;
            this.currentPokemonSprite.position = {
                x: 8,
                y: 16,
            };
            this.currentPokemonSprite.draw();
        }
    }

    drawCurrentBoxPokemon() {
        this.stateMachine.player.pcPokemon[this.stateMachine.pcMenu.box].forEach((row, i) => {
            row.forEach((pokemon, j) => {
                if (this.boxIcons[i][j] === null) {
                    this.boxIcons[i][j] = new PokemonIcon({
                        pokemon: pokemon,
                        position: {
                            x: 80 + 24 * j,
                            y: 24 + 20 * i,
                        },
                    });
                } else {
                    this.boxIcons[i][j].position = {
                        x: 80 + 24 * j,
                        y: 24 + 20 * i,
                    };
                    this.boxIcons[i][j].pokemon = pokemon;
                }
                this.boxIcons[i][j].draw();
            });
        });
    }

    drawSelectedPokemonIconShadow() {
        if (
            this.stateMachine.pcMenu.selectedPokemon instanceof Pokemon &&
            this.selectedPokemonIcon !== null
        ) {
            this.selectedPokemonIconShadow.position = {
                x: this.selectedPokemonIcon.position.x + 13,
                y: this.selectedPokemonIcon.position.y + 28,
            };
            this.selectedPokemonIconShadow.draw();
        }
    }

    drawSelectedPokemonIcon() {
        if (this.stateMachine.pcMenu.selectedPokemon instanceof Pokemon) {
            if (this.selectedPokemonIcon === null) {
                this.selectedPokemonIcon = new PokemonIcon({
                    pokemon: this.stateMachine.pcMenu.selectedPokemon,
                    position: {
                        x: this.cursor.position.x - 6,
                        y: this.cursor.position.y + 6,
                    },
                });
            }

            this.selectedPokemonIcon.position = {
                x: this.cursor.position.x - 6,
                y: this.cursor.position.y + 6,
            };
            this.selectedPokemonIcon.pokemon = this.stateMachine.pcMenu.selectedPokemon;

            this.selectedPokemonIcon.draw();
        }
    }

    drawPartyPokemon() {
        this.stateMachine.pcMenu.partyList.forEach((pokemon, index) => {
            if (pokemon === null) {
                this.partyIcons[index] = null;
            } else {
                if (this.partyIcons[index] === null) {
                    this.partyIcons[index] = new PokemonIcon({
                        pokemon: pokemon,
                        position: {
                            x: 80 + 24 * index,
                            y: 120,
                        },
                    });
                } else {
                    this.partyIcons[index].pokemon = pokemon;
                    this.partyIcons[index].position = {
                        x: 80 + 24 * index,
                        y: 120,
                    };
                }
            }
            if (this.partyIcons[index] !== null) {
                this.partyIcons[index].draw();
            }
        });
    }

    currentIcon() {
        const currentSection = this.stateMachine.pcMenu.currentSection;
        if (this.stateMachine.pcMenu.currentSection === 0) {
            return null;
        } else if (currentSection === 1) {
            return this.boxIcons[this.stateMachine.pcMenu.row][this.stateMachine.pcMenu.column];
        } else if (currentSection === 2) {
            return this.partyIcons[this.stateMachine.pcMenu.column];
        }
    }

    drawBoxArrows() {
        const currentSection = this.stateMachine.pcMenu.currentSection;
        this.boxArrows.moving = true;
        this.boxArrows.hold = 4;
        if (currentSection !== 0) {
            this.boxArrows.moving = false;
            this.boxArrows.frames.val = 0;
        }
        this.boxArrows.draw();
    }

    drawPCElectricity() {
        if (this.stateMachine.pcMenu.currentPokemon instanceof Pokemon) {
            this.pcElectricityOn.draw();
        } else {
            this.pcElectricityOff.draw();
        }
    }

    draw() {
        if (this.stateMachine !== getOSM()) {
            this.stateMachine = getOSM();
        }
        if (!this.visible || !this.stateMachine.context.currentPC.active) {
            return;
        }

        this.boxTitle.text = `Box ${this.stateMachine.pcMenu.box + 1}`;
        this.currentPokemonNameText.text = this.stateMachine.pcMenu.currentPokemon?.name ?? '';
        this.currentPokemonLevelText.text = this.stateMachine.pcMenu.currentPokemon
            ? `Lv. ${this.stateMachine.pcMenu.currentPokemon?.level}`
            : '';
        this.currentPokemonNatureText.text = this.stateMachine.pcMenu.currentPokemon
            ? `${this.stateMachine.pcMenu.currentPokemon?.nature.displayName()} Nature`
            : '';
        this.currentPokemonNatureSummaryText.text = this.stateMachine.pcMenu.currentPokemon
            ? this.stateMachine.pcMenu.currentPokemon?.nature.shortStatSummary()
            : '';

        this.calculateCursorPosition();
        this.updateCursorPosition();
        this.cursor.position = this.currentCursorPosition;

        this.background.draw();
        this.boxTitle.draw();
        this.drawBoxArrows();
        this.drawPCElectricity();
        this.currentPokemonNameText.draw();
        this.currentPokemonLevelText.draw();
        this.currentPokemonNatureText.draw();
        this.currentPokemonNatureSummaryText.draw();
        if (this.stateMachine.pcMenu.currentPokemon) {
            this.typeBadge1.type = this.stateMachine.pcMenu.currentPokemon.type1;
            this.typeBadge2.type = this.stateMachine.pcMenu.currentPokemon.type2;
            if (
                this.stateMachine.pcMenu.currentPokemon.type1 ===
                this.stateMachine.pcMenu.currentPokemon.type2
            ) {
                this.typeBadge1.position = { x: 4, y: 90 };
                this.typeBadge1.draw();
            } else {
                this.typeBadge1.position = { x: 4, y: 90 };
                this.typeBadge2.position = { x: 4 + 38, y: 90 };
                this.typeBadge1.draw();
                this.typeBadge2.draw();
            }
        }
        this.drawSelectedPokemonIconShadow();
        this.drawPartyPokemon();
        this.drawCurrentBoxPokemon();
        this.drawCurrentPokemon();
        this.drawSelectedPokemonIcon();
        this.cursor.frames.val = this.stateMachine.pcMenu.selectedPokemon instanceof Pokemon ? 1 : 0;
        this.cursor.draw();
        this.pokemonPreselectedMenu.selectedOption = this.stateMachine.pcMenu.preselectionOption;
        if (this.stateMachine.currentState === PC_POKEMON_PRESELECTED) {
            this.pokemonPreselectedMenu.draw();
        }
    }
}
